import React, { useEffect, useState, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Pagination,
  Skeleton,
  Empty,
  Badge,
  Tooltip,
  message,
  Dropdown,
  Menu
} from 'antd';
import {
  PlusCircleOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  FileImageOutlined,
  TeamOutlined,
  ShareAltOutlined,
  ReloadOutlined,
  InboxOutlined,
  UndoOutlined,
  HistoryOutlined,
  AppstoreOutlined,
  SearchOutlined,
  CameraOutlined,
  VideoCameraOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  MoreOutlined,
  EllipsisOutlined,
  EditOutlined,
  RightOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getProjects, updateProject, archiveProject, unarchiveProject } from '../../services/projectServices';
import { useDispatch, useSelector } from "react-redux";
import FolderIMage from "../../assets/images/blue-directory-icon.png"
import moment from 'moment';
import ButtonBar from "../../components/ButtonNavigation"
import PagesHeader from "../../components/PageHeader";
import AssignInstallersModal from "./modals/AssignInstallersModal";
import ProjectHistoryDrawer from "./modals/ProjectHistoryDrawer";

const { Search } = Input;

const isProjectArchived = (p) => {
  return Boolean(p?.is_archived || p?.archived_at);
};

const isRecentProject = (p, index) => {
  if (index === 0) return true;
  if (!p?.created_at) return false;
  return moment().diff(moment(p.created_at), 'days') <= 7;
};

const ProjectList = () => {
  const [projects, setProjects] = useState([]);
  const [originalProjectData, setOriginalProjectData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'archived'
  const [activeCount, setActiveCount] = useState(0);
  const [archivedCount, setArchivedCount] = useState(0);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [selectedProjectForAssign, setSelectedProjectForAssign] = useState(null);
  const [historyDrawerVisible, setHistoryDrawerVisible] = useState(false);
  const [selectedProjectForHistory, setSelectedProjectForHistory] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0
  });

  const activeTabRef = useRef('active');
  const requestIdRef = useRef(0);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state?.User?.userDetails);
  const isDealer = currentUser?.user_role === 'um_trusted-dealer';
  const isInstaller = currentUser?.user_role === 'um_installer';
  const isTeamRole = isDealer || isInstaller;

  const fetchProjects = (page = 1, search = '', tab = activeTabRef.current) => {
    setLoading(true);
    const requestId = ++requestIdRef.current;

    const params = {
      page,
      archived: tab === 'archived' ? 1 : 0,
      ...(search && { search })
    };

    return dispatch(getProjects(params))
      .then((response) => {
        // Prevent race condition: if user switched tab or newer search fired, ignore stale response
        if (requestId !== requestIdRef.current || tab !== activeTabRef.current) {
          return;
        }

        const rawData = response?.data || [];

        // Accurately partition projects by active vs archived
        const activeList = rawData.filter((p) => !isProjectArchived(p));
        const archivedList = rawData.filter((p) => isProjectArchived(p));

        // Accurately set counts for both Active and Archived tabs
        if (typeof response?.active_count === 'number') {
          setActiveCount(response.active_count);
        } else if (tab === 'active') {
          setActiveCount(response?.total || activeList.length);
        }

        if (typeof response?.archived_count === 'number') {
          setArchivedCount(response.archived_count);
        } else if (tab === 'archived') {
          setArchivedCount(response?.total || archivedList.length);
        } else {
          // If backend didn't supply counts yet, query archived count directly so badge appears
          dispatch(getProjects({ page: 1, per_page: 1, archived: 1 }))
            .then((archivedRes) => {
              const count = typeof archivedRes?.total === 'number' ? archivedRes.total : (archivedRes?.data?.length || 0);
              setArchivedCount(count);
            })
            .catch(() => {});
        }

        // Show only projects strictly matching the current tab
        let displayList = tab === 'archived' ? archivedList : activeList;
        if (search) {
          displayList = displayList.filter((item) => (
            item?.name?.toLowerCase()?.includes(search.toLowerCase()) ||
            item?.address?.toLowerCase()?.includes(search.toLowerCase()) ||
            item?.description?.toLowerCase()?.includes(search.toLowerCase())
          ));
        }

        setProjects(displayList);
        if (!search) {
          setOriginalProjectData(rawData);
        }

        setPagination({
          current: response?.current_page || page,
          pageSize: response?.per_page || 15,
          total: typeof response?.total === 'number' ? response.total : displayList.length
        });
        setLoading(false);
        return response;
      })
      .catch((error) => {
        if (requestId === requestIdRef.current) {
          message.error('Failed to fetch projects');
          setLoading(false);
        }
      });
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchProjects(pagination.current, searchValue, activeTabRef.current)
      .then(() => {
        message.success('Projects refreshed');
      })
      .finally(() => {
        setTimeout(() => setIsRefreshing(false), 400);
      });
  };

  const handleTabChange = (newTab) => {
    if (newTab === activeTab) return;
    activeTabRef.current = newTab;
    setActiveTab(newTab);
    setSearchValue('');
    setProjects([]);
    fetchProjects(1, '', newTab);
  };

  const handlePageChange = (page) => {
    fetchProjects(page, searchValue, activeTab);
  };

  useEffect(() => {
    fetchProjects(1, '', 'active');
  }, []);

  const onSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchValue(value);

    if (value !== '') {
      if (value.length >= 2) {
        fetchProjects(1, value, activeTab);
      } else {
        const baseList = originalProjectData?.filter((p) =>
          activeTab === 'archived' ? isProjectArchived(p) : !isProjectArchived(p)
        );
        const filteredData = baseList?.filter((filteritem) => (
          filteritem?.name?.toLowerCase()?.includes(value) ||
          filteritem?.address?.toLowerCase()?.includes(value) ||
          filteritem?.description?.toLowerCase()?.includes(value)
        ));
        setProjects(filteredData || []);
      }
    } else {
      fetchProjects(1, '', activeTab);
    }
  };

  const handleRenameProject = (e, project) => {
    if (e?.domEvent?.stopPropagation) e.domEvent.stopPropagation();
    else if (e?.stopPropagation) e.stopPropagation();

    Swal.fire({
      title: 'Rename Project',
      input: 'text',
      inputValue: project.name || '',
      inputPlaceholder: 'Enter project name',
      showCancelButton: true,
      confirmButtonText: 'Rename',
      confirmButtonColor: '#0a497b',
      cancelButtonColor: '#64748b',
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return 'Project name cannot be empty!';
        }
      }
    }).then(async (result) => {
      if (result.isConfirmed && result.value && result.value.trim() !== project.name) {
        setLoading(true);
        try {
          await dispatch(updateProject(project.id, { name: result.value.trim() }));
          message.success('Project renamed successfully');
        } catch (err) {
          message.error('Failed to rename project');
        } finally {
          fetchProjects(pagination.current, searchValue, activeTab);
        }
      }
    });
  };

  const handleArchiveProject = (e, project) => {
    if (e?.domEvent?.stopPropagation) e.domEvent.stopPropagation();
    else if (e?.stopPropagation) e.stopPropagation();
    Swal.fire({
      title: 'Archive Project?',
      text: `Are you sure you want to archive "${project.name}"? It will be moved to Archived Projects.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, Archive it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          await dispatch(archiveProject(project.id));
          message.success('Project archived successfully');
        } catch (err) {
          message.error('Failed to archive project');
        } finally {
          fetchProjects(pagination.current, searchValue, activeTab);
        }
      }
    });
  };

  const handleUnarchiveProject = (e, project) => {
    if (e?.domEvent?.stopPropagation) e.domEvent.stopPropagation();
    else if (e?.stopPropagation) e.stopPropagation();
    Swal.fire({
      title: 'Restore Project?',
      text: `Do you want to restore "${project.name}" back to Active projects?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0284c7',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, Restore to Active!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          await dispatch(unarchiveProject(project.id));
          message.success('Project restored to active');
        } catch (err) {
          message.error('Failed to restore project');
        } finally {
          fetchProjects(pagination.current, searchValue, activeTab);
        }
      }
    });
  };

  const handleOpenHistory = (e, project) => {
    if (e?.domEvent?.stopPropagation) e.domEvent.stopPropagation();
    else if (e?.stopPropagation) e.stopPropagation();
    setSelectedProjectForHistory(project);
    setHistoryDrawerVisible(true);
  };

  return (
    <div className="PhotoWrapper">
      <div className="PhotoHeader">
        <PagesHeader title="Photo Projects" />
      </div>

      <div className="container-xxl px-xl-5 px-lg-3">
        <div className="button-navigations mt-5">
          <ButtonBar />
        </div>
      </div>

      <div className="container-xxl px-xl-5 px-lg-3" style={{ padding: "40px 24px" }}>
        {/* Modern Header Section with Active/Archived Tabs */}
        <div className="projects-page-header" style={{ marginBottom: 24 }}>
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col xs={24} md={13}>
              <div className="header-text-group">
                <h1 className="header-title">Projects</h1>
                <p className="header-subtitle">
                  Manage your flooring inspection projects and photos
                </p>
              </div>
            </Col>

            {/* Active vs Archived Sliding Capsule Switch */}
            <Col xs={24} md={11} className="header-switch-col">
              <div className="sliding-pill-switch">
                <span
                  className={`sliding-pill-glider ${activeTab === 'archived' ? 'slide-right' : ''}`}
                />
                <button
                  type="button"
                  className={`sliding-pill-btn ${activeTab === 'active' ? 'active' : ''}`}
                  onClick={() => handleTabChange('active')}
                >
                  <AppstoreOutlined className="pill-btn-icon" />
                  <span className="pill-btn-label-desktop">Active Projects</span>
                  <span className="pill-btn-label-mobile">Active</span>
                  {activeCount > 0 && (
                    <span className={`pill-count-badge ${activeTab === 'active' ? 'active' : ''}`}>
                      {activeCount}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className={`sliding-pill-btn ${activeTab === 'archived' ? 'active' : ''}`}
                  onClick={() => handleTabChange('archived')}
                >
                  <InboxOutlined className="pill-btn-icon" />
                  <span className="pill-btn-label-desktop">Archived Projects</span>
                  <span className="pill-btn-label-mobile">Archived</span>
                  {archivedCount > 0 && (
                    <span className={`pill-count-badge ${activeTab === 'archived' ? 'active' : ''}`}>
                      {archivedCount}
                    </span>
                  )}
                </button>
              </div>
            </Col>
          </Row>
        </div>

        {/* Modern Search and Action Toolbar */}
        <div className="projects-toolbar-row" style={{ marginBottom: 32 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col flex="auto">
              <div className="modern-search-wrapper">
                <Input
                  prefix={<SearchOutlined className="search-prefix-icon" />}
                  placeholder={activeTab === 'archived' ? "Search archived projects by address, city or name..." : "Search by address, city or project name..."}
                  allowClear
                  onChange={onSearch}
                  value={searchValue}
                  className="modern-search-input"
                />
              </div>
            </Col>

            {/* Modern Refresh Button with Label & Smooth Loader */}
            <Col>
              <button
                type="button"
                className={`modern-action-btn refresh-btn ${isRefreshing ? 'is-loading' : ''}`}
                onClick={handleRefresh}
                disabled={isRefreshing}
                aria-label="Refresh Projects"
              >
                <ReloadOutlined spin={isRefreshing} className="action-btn-icon" />
                <span className="action-btn-label">
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </span>
              </button>
            </Col>

            {/* Modern New Project Button */}
            {activeTab === 'active' && (
              <Col>
                <button
                  type="button"
                  className="modern-action-btn new-project-btn"
                  onClick={() => navigate("/projects/create")}
                >
                  <PlusCircleOutlined className="action-btn-icon" />
                  <span>New Project</span>
                </button>
              </Col>
            )}
          </Row>
        </div>

        {loading ? (
          <Row gutter={[24, 24]}>
            {Array.from({ length: 6 }).map((_, idx) => (
              <Col key={idx} xs={24} md={12} lg={8}>
                <Card className="project-grid-card" style={{ padding: 0 }}>
                  <Skeleton.Image
                    active
                    className="skeleton-image-full"
                    style={{ width: "100%", height: 200 }}
                  />
                  <div style={{ padding: 20 }}>
                    <Skeleton active paragraph={{ rows: 2 }} />
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        ) : projects.length === 0 ? (
          <div style={{
            padding: '80px 24px',
            textAlign: 'center',
            background: '#ffffff',
            borderRadius: '16px',
            border: '2px dashed #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
          }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 17, fontWeight: 600, color: '#1e293b' }}>
                    {searchValue
                      ? 'No projects match your search'
                      : activeTab === 'archived'
                        ? 'No Archived Projects'
                        : 'No projects found'}
                  </span>
                  <span style={{ fontSize: 13, color: '#64748b', maxWidth: 420 }}>
                    {searchValue
                      ? 'Try checking for typos or searching with different keywords.'
                      : activeTab === 'archived'
                        ? 'Projects you archive will safely appear here. They are hidden from active lists, dropdowns, and inspection reports.'
                        : 'Get started by creating your very first flooring inspection project.'}
                  </span>
                </div>
              }
            >
              {!searchValue && activeTab === 'active' && (
                <Button
                  className="btn-new-project"
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  onClick={() => navigate('/projects/create')}
                  style={{ marginTop: 16 }}
                >
                  Create Your First Project
                </Button>
              )}
            </Empty>
          </div>
        ) : (
          <>
            <Row gutter={[32, 32]}>
              {projects.map((p, index) => (
                <Col key={p.id} xs={24} md={12} lg={8}>
                  <Card
                    className="project-grid-card"
                    hoverable
                    cover={
                      <div style={{ position: "relative" }}>
                        {p.inspection_images?.[0]?.image_url ? (
                          <img
                            alt={p.name}
                            src={p.inspection_images[0].image_url}
                            style={{ height: 220, width: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <div className="no-photo-placeholder">
                            <FileImageOutlined style={{ fontSize: 40 }} />
                            <span>NO PHOTOS ADDED</span>
                          </div>
                        )}
                        {/* Top-Left: Diagonal Corner Ribbon "New" */}
                        {isRecentProject(p, index) && !isProjectArchived(p) && (
                          <div className="project-card-corner-ribbon-wrap">
                            <div className="project-card-corner-ribbon">New</div>
                          </div>
                        )}
                        {isProjectArchived(p) && (
                          <div className="project-archived-overlay-badge">
                            <InboxOutlined /> Archived
                          </div>
                        )}
                        {/* Top-Right: Floating Dots Action Menu */}
                        <div
                          className="project-card-floating-menu"
                          onClick={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                          onTouchEnd={(e) => e.stopPropagation()}
                        >
                          <Dropdown
                            overlay={
                              <Menu
                                className="project-card-action-dropdown-menu"
                                onClick={({ domEvent }) => domEvent && domEvent.stopPropagation()}
                              >
                                <Menu.Item
                                  key="rename"
                                  onClick={(e) => handleRenameProject(e.domEvent, p)}
                                >
                                  <div className="custom-menu-item">
                                    <span className="item-icon-box rename"><EditOutlined /></span>
                                    <span className="item-label">Rename Project</span>
                                  </div>
                                </Menu.Item>
                                <Menu.Item
                                  key="view"
                                  onClick={() => navigate(`/project-detail/?project_id=${p.id}`)}
                                >
                                  <div className="custom-menu-item">
                                    <span className="item-icon-box view"><EyeOutlined /></span>
                                    <span className="item-label">View Details</span>
                                  </div>
                                </Menu.Item>
                                <Menu.Item
                                  key="history"
                                  onClick={(e) => handleOpenHistory(e.domEvent, p)}
                                >
                                  <div className="custom-menu-item">
                                    <span className="item-icon-box history"><HistoryOutlined /></span>
                                    <span className="item-label">Project History</span>
                                  </div>
                                </Menu.Item>
                                <Menu.Divider className="custom-menu-divider" />
                                {isProjectArchived(p) ? (
                                  <Menu.Item
                                    key="restore"
                                    onClick={(e) => handleUnarchiveProject(e.domEvent, p)}
                                  >
                                    <div className="custom-menu-item">
                                      <span className="item-icon-box restore"><UndoOutlined /></span>
                                      <span className="item-label">Restore to Active</span>
                                    </div>
                                  </Menu.Item>
                                ) : (
                                  <Menu.Item
                                    key="archive"
                                    onClick={(e) => handleArchiveProject(e.domEvent, p)}
                                  >
                                    <div className="custom-menu-item danger">
                                      <span className="item-icon-box archive"><InboxOutlined /></span>
                                      <span className="item-label">Archive Project</span>
                                    </div>
                                  </Menu.Item>
                                )}
                              </Menu>
                            }
                            trigger={['click']}
                            placement="bottomRight"
                            overlayClassName="project-card-action-dropdown-overlay"
                            destroyPopupOnHide={true}
                            transitionName=""
                            getPopupContainer={() => document.body}
                          >
                            <button
                              type="button"
                              className="floating-menu-trigger-btn"
                              aria-label="Project actions"
                              style={{ touchAction: "manipulation" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <EllipsisOutlined style={{ fontSize: 18 }} />
                            </button>
                          </Dropdown>
                        </div>
                      </div>
                    }
                    onClick={() => navigate(`/project-detail/?project_id=${p.id}`)}
                  >
                    {/* Creator & Share Bar (Only for Trusted Dealer and Installers) */}
                    {isTeamRole && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 10,
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        {p.is_owner ? (
                          <span
                            style={{
                              fontSize: "11.5px",
                              fontWeight: 600,
                              color: "#0369a1",
                              background: "#e0f2fe",
                              padding: "2px 8px",
                              borderRadius: "6px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            🏷️ Created by You
                          </span>
                        ) : isInstaller ? (
                          <span
                            style={{
                              fontSize: "11.5px",
                              fontWeight: 600,
                              color: "#059669",
                              background: "#ecfdf5",
                              padding: "2px 8px",
                              borderRadius: "6px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            🏢 Assigned by Dealer
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: "11.5px",
                              fontWeight: 600,
                              color: "#4338ca",
                              background: "#eef2ff",
                              padding: "2px 8px",
                              borderRadius: "6px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            👷 {p.creator_name || "Installer"}
                          </span>
                        )}

                        {/* Dealer Share / Assign Button */}
                        {isDealer && (
                          <Button
                            size="small"
                            icon={<TeamOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProjectForAssign(p);
                              setAssignModalVisible(true);
                            }}
                            style={{
                              fontSize: "11.5px",
                              height: "26px",
                              borderRadius: "6px",
                              borderColor: "#bae6fd",
                              color: "#0284c7",
                              background: "#f0f9ff",
                              fontWeight: 600,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "0 8px",
                            }}
                          >
                            Share {p.assigned_installers_count > 0 ? `(${p.assigned_installers_count})` : ""}
                          </Button>
                        )}
                      </div>
                    )}

                    <div className="project-card-title">{p.name}</div>
                    
                    <div className="project-card-info">
                      <div className="info-item address">
                        <EnvironmentOutlined />
                        <span>{p.address}, {p.city}, {p.state} {p.postal_code}</span>
                      </div>
                    </div>

                    <div className="project-card-footer">
                      <div className="date-text">
                        <CalendarOutlined />
                        {moment(p.created_at).format("MMM DD, YYYY")}
                      </div>
                      <div className="card-stats">
                        <Tooltip title={`${p.inspection_images?.length || 0} Photos`}>
                          <div className="stat-pill photos">
                            <CameraOutlined style={{ fontSize: 16 }} />
                            <span>{p.inspection_images?.length || 0}</span>
                          </div>
                        </Tooltip>
                        <Tooltip title={`${p.inspection_videos?.length || 0} Videos`}>
                          <div className="stat-pill videos">
                            <VideoCameraOutlined style={{ fontSize: 16 }} />
                            <span>{p.inspection_videos?.length || 0}</span>
                          </div>
                        </Tooltip>
                        <Tooltip title={`${p.documents_count ?? p.project_documents?.length ?? 0} Documents`}>
                          <div className="stat-pill documents">
                            <FilePdfOutlined style={{ fontSize: 16 }} />
                            <span>{p.documents_count ?? p.project_documents?.length ?? 0}</span>
                          </div>
                        </Tooltip>
                        <Tooltip title={`${p.reports_count ?? p.reports?.length ?? 0} Project Photo Reports`}>
                          <div className="stat-pill reports">
                            <FileImageOutlined style={{ fontSize: 16 }} />
                            <span>{p.reports_count ?? p.reports?.length ?? 0}</span>
                          </div>
                        </Tooltip>
                      </div>
                    </div>

                    {/* Modern Pill Action Buttons (Matching Card Aesthetic) */}
                    <div
                      className="project-card-actions-bar"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="project-card-action-btn history"
                        onClick={(e) => handleOpenHistory(e, p)}
                      >
                        <HistoryOutlined className="action-icon" />
                        <span>History</span>
                      </button>

                      {isProjectArchived(p) ? (
                        <button
                          type="button"
                          className="project-card-action-btn restore"
                          onClick={(e) => handleUnarchiveProject(e, p)}
                        >
                          <UndoOutlined className="action-icon" />
                          <span>Restore to Active</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="project-card-action-btn archive"
                          onClick={(e) => handleArchiveProject(e, p)}
                        >
                          <InboxOutlined className="action-icon" />
                          <span>Archive</span>
                        </button>
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            <Row justify="center" style={{ marginTop: 60 }}>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={handlePageChange}
                showSizeChanger={false}
                className="custom-pagination"
              />
            </Row>
          </>
        )}
      </div>

      {/* Assign / Share Installers Modal */}
      <AssignInstallersModal
        visible={assignModalVisible}
        onClose={() => setAssignModalVisible(false)}
        project={selectedProjectForAssign}
        onUpdated={() => fetchProjects(pagination.current, searchValue, activeTab)}
      />

      {/* Project History Drawer */}
      <ProjectHistoryDrawer
        visible={historyDrawerVisible}
        onClose={() => setHistoryDrawerVisible(false)}
        projectId={selectedProjectForHistory?.id}
        projectName={selectedProjectForHistory?.name}
      />
    </div>
  );
};

export default ProjectList;
